import { Card, Input } from '@platform/design-system';
import { useId, useState } from 'react';
import type { GeoNames } from '../geography/useGeoAreas.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  foldedName,
  heatOf,
  HEAT_STEPS,
  rankedGovernoratesOf,
  type GovernorateFigures,
  type RankedGovernorate,
} from './governorate-rates.ts';
import type { StatisticsFormat } from './statistics.ts';
import { GOVERNORATE_SHAPES, MAP_SCALE, MAP_VIEW_BOX } from './tunisia-governorates.ts';
import styles from './Statistics.module.css';

export type GovernorateMapProps = {
  governorates: readonly GovernorateFigures[];
  nameOf: GeoNames;
  carrierNameOf: (code: string) => string;
  format: StatisticsFormat;
};

const HEAT_LEVELS = Array.from({ length: HEAT_STEPS }, (_unused, index) => index + 1);

const fillOf = (heat: number | null): string =>
  heat === null ? 'var(--color-surfaceSunken)' : `var(--color-heat${String(heat)})`;

export const GovernorateMap = ({ governorates, nameOf, carrierNameOf, format }: GovernorateMapProps) => {
  const { t } = usePageContext();
  const detailId = useId();
  const [active, setActive] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const ranked = rankedGovernoratesOf(governorates);
  const byCode = new Map(ranked.map((governorate) => [governorate.code, governorate]));
  const shown = active === null ? undefined : byCode.get(active);
  const listed = ranked.filter((governorate) =>
    foldedName(nameOf(governorate.code)).includes(foldedName(search)),
  );
  const labelOf = (code: string) => {
    const governorate = byCode.get(code);
    return governorate === undefined
      ? t('statistics.mapNoParcels', { name: nameOf(code) })
      : t('statistics.mapLabel', { name: nameOf(code), rate: format.rate(governorate.rate) });
  };
  const leave = () => {
    setActive(null);
  };
  const detail = (governorate: RankedGovernorate) => (
    <dl className={styles['mapDetail']}>
      <dt>{t('statistics.parcels')}</dt>
      <dd>{format.count(governorate.parcels)}</dd>
      <dt>{t('statistics.parcel_delivered')}</dt>
      <dd>{format.count(governorate.delivered)}</dd>
      <dt>{t('statistics.parcel_returned')}</dt>
      <dd>{format.count(governorate.returned)}</dd>
      <dt>{t('statistics.averageDelay')}</dt>
      <dd>{format.days(governorate.averageDays)}</dd>
      <dt>{t('statistics.bestCarrier')}</dt>
      <dd>{governorate.bestCarrier === null ? '—' : carrierNameOf(governorate.bestCarrier)}</dd>
    </dl>
  );
  return (
    <Card title={t('statistics.mapTitle')}>
      <div className={styles['legend']} aria-hidden="true">
        <span>{t('statistics.mapLow')}</span>
        {HEAT_LEVELS.map((level) => (
          <i key={level} style={{ background: fillOf(level) }} />
        ))}
        <span>{t('statistics.mapHigh')}</span>
      </div>
      <div className={styles['mapGrid']}>
        <svg
          className={styles['map']}
          viewBox={`0 0 ${String(MAP_VIEW_BOX.width)} ${String(MAP_VIEW_BOX.height)}`}
          role="group"
          aria-label={t('statistics.mapLabelAll')}
          aria-describedby={detailId}
        >
          {GOVERNORATE_SHAPES.map((shape) => (
            <path
              key={shape.code}
              className={styles['governorate']}
              d={shape.path}
              transform={`scale(${String(MAP_SCALE)})`}
              fill={fillOf(heatOf(byCode.get(shape.code)?.rate ?? null))}
              data-active={active === shape.code}
              tabIndex={0}
              role="img"
              aria-label={labelOf(shape.code)}
              onMouseEnter={() => {
                setActive(shape.code);
              }}
              onFocus={() => {
                setActive(shape.code);
              }}
              onMouseLeave={leave}
              onBlur={leave}
            />
          ))}
        </svg>
        <div className={styles['mapSide']}>
          <div id={detailId} className={styles['mapCard']} aria-live="polite">
            {shown === undefined ? (
              <p className={styles['muted']}>{t('statistics.mapHint')}</p>
            ) : (
              <>
                <strong>
                  {nameOf(shown.code)} · {format.rate(shown.rate)}
                </strong>
                {detail(shown)}
                {shown.rank === null ? null : (
                  <span className={styles['muted']}>
                    {t('statistics.mapRank', { rank: shown.rank, total: ranked.length })}
                  </span>
                )}
              </>
            )}
          </div>
          <Input
            label={t('statistics.mapSearch')}
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          <ol className={styles['ranking']}>
            {listed.map((governorate) => (
              <li key={governorate.code}>
                <button
                  type="button"
                  className={styles['rankRow']}
                  data-active={active === governorate.code}
                  onMouseEnter={() => {
                    setActive(governorate.code);
                  }}
                  onFocus={() => {
                    setActive(governorate.code);
                  }}
                  onMouseLeave={leave}
                  onBlur={leave}
                >
                  <span className={styles['rankNumber']}>{governorate.rank ?? '—'}</span>
                  <span>{nameOf(governorate.code)}</span>
                  <span
                    className={styles['swatch']}
                    style={{ background: fillOf(heatOf(governorate.rate)) }}
                    aria-hidden="true"
                  />
                  <strong className={styles['figure']}>{format.rate(governorate.rate)}</strong>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Card>
  );
};
